/**
 * WP9a settings landing — tools-menu destination inside the verification shell.
 *
 * Heavy settings stay full pages (existing `/settings/...` leaves). This view is
 * the group index only. It does not mount the old right-context rail and does not
 * rewrite connection/repo detail (WP9b/c).
 */

import { useNavigate } from "@tanstack/react-router";
import { PageFrame } from "./pages/page-frame";
import { FrameworkSettings } from "./framework-settings";
import { useVerificationEnvOptional } from "./env-provider";
import {
  compatibilitySettingsGroups,
  launchSettingsGroups,
  settingsPageHref,
  type SettingsLandingGroup,
} from "./settings-nav";

export function SettingsLanding() {
  const navigate = useNavigate();
  const go = (to: string) => {
    void navigate({ to, resetScroll: true });
  };
  const launch = launchSettingsGroups();
  const compatibility = compatibilitySettingsGroups();
  const env = useVerificationEnvOptional();

  return (
    <PageFrame
      page="settings"
      title="Settings"
      lede="Account, workspace, appearance, integrations, agents, and advanced configuration. Every settings page stays in this app."
    >
      <div data-verification-settings="landing">
        <div className="vp-card" data-verification-env-reset="">
          <div className="vp-card-head">
            <h2 className="vp-card-title">Environment scope</h2>
          </div>
          <div className="vp-card-body">
            <button
              type="button"
              className="vp-button"
              data-action-id="shell.env-reset"
              data-env-reset=""
              onClick={() => env?.resetEnvironment()}
            >
              Reset environment
            </button>
          </div>
        </div>
        <FrameworkSettings onNavigate={go} />
        <GroupGrid kind="launch" groups={launch} onNavigate={go} />
        <h2 className="vp-card-title" style={{ margin: "calc(var(--spacing) * 6) 0 calc(var(--spacing) * 3)" }}>
          Compatibility
        </h2>
        <p className="vp-lede" style={{ marginBottom: "calc(var(--spacing) * 3)" }}>
          Direct-route groups kept reachable. Not primary launch IA (HG4).
        </p>
        <GroupGrid kind="compatibility" groups={compatibility} onNavigate={go} />
      </div>
    </PageFrame>
  );
}

function GroupGrid({
  kind,
  groups,
  onNavigate,
}: {
  kind: "launch" | "compatibility";
  groups: SettingsLandingGroup[];
  onNavigate: (to: string) => void;
}) {
  return (
    <div className="vp-grid" data-cols="2" data-settings-groups={kind}>
      {groups.map((group) => (
        <article
          key={group.key}
          className="vp-card"
          data-settings-group={group.key}
          data-settings-kind={group.kind}
        >
          <div className="vp-card-head">
            <button
              type="button"
              className="vp-card-title"
              data-action-id="shell.settings-group"
              onClick={() => onNavigate(settingsPageHref(group.href))}
            >
              {group.label}
            </button>
          </div>
          <div className="vp-card-body">
            <ul>
              {group.items.map((item) => {
                const href = settingsPageHref(item);
                return (
                  <li key={item.href}>
                    <a
                      href={href}
                      onClick={(event) => {
                        event.preventDefault();
                        onNavigate(href);
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </article>
      ))}
    </div>
  );
}
